/**
 * Componente invisible que se monta una vez globalmente.
 *
 * Funcion: cuando el backend tiene notificaciones con `toastShownAt = NULL`,
 * las muestra como toast UNA SOLA VEZ y llama al endpoint para marcarlas.
 *
 * Esto garantiza que el toast se muestre:
 *   - exactamente 1 vez
 *   - en cualquier dispositivo que abra la app
 *   - sin depender de localStorage
 *
 * IMPORTANTE: NO llamar refreshCount() desde aca.
 * El polling global (cada 20s) actualiza el badge. Si llamamos desde aca,
 * creamos un loop infinito (pendingToasts cambia -> effect re-corre -> fetch).
 */
import { useEffect, useRef } from "react"
import { useNotifications } from "./NotificationContext"
import { useToast } from "../components/Toast.jsx"

export default function ToastPoller() {
  const { pendingToasts } = useNotifications()
  const { toast } = useToast()
  const enProcesoRef = useRef(false)

  useEffect(() => {
    if (enProcesoRef.current) return
    if (!pendingToasts || pendingToasts <= 0) return

    let cancelled = false
    async function mostrar() {
      enProcesoRef.current = true
      try {
        const token = localStorage.getItem("token") || ""
        if (!token) return

        const res = await fetch("/api/notifications?pendingToast=true&perPage=5", {
          headers: { Authorization: "Bearer " + token },
        })
        if (!res.ok) return
        const data = await res.json()
        const items = data.items || []

        for (const n of items) {
          if (cancelled) return
          // Marcar como mostrada ANTES de mostrar (para no duplicar si
          // el usuario refresca en el medio)
          try {
            await fetch("/api/notifications/" + n.id + "/toast-shown", {
              method: "POST",
              headers: { Authorization: "Bearer " + token },
            })
          } catch (_) { /* noop: el polling global lo reintenta */ }
          // Mostrar toast
          if (n.tipo === "success") {
            toast.success(n.titulo, { description: n.descripcion, duration: 6000 })
          } else if (n.tipo === "error") {
            toast.error(n.titulo, { description: n.descripcion })
          } else {
            toast.info(n.titulo, { description: n.descripcion })
          }
        }
        // NO llamamos refreshCount() aca: el polling global (cada 20s)
        // actualiza el badge. Llamar desde aca crea loop infinito.
      } finally {
        enProcesoRef.current = false
      }
    }
    mostrar()
    return () => { cancelled = true }
  }, [pendingToasts, toast])

  return null
}
